import React from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Check, LifeBuoy, Mail, MessageCircleQuestion } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { labelsFor } from '@constants/mimeLabels';
import { useCapabilities } from '@hooks/useCapabilities';
import {
  HELP_CONTACT_EMAIL,
  HELP_FAQ_IDS,
  HELP_FEATURES,
  HELP_TIP_KEYS,
} from '@constants/help';
import { selectDesignVerificationEnabled } from '@features/featureFlags/featureFlagsSlice';
import { useAppSelector } from '@store/index';

const HelpPage: React.FC = () => {
  const { t } = useTranslation();
  // The page used to enumerate these in prose, which could not follow the allowlist.
  const { capabilities, status } = useCapabilities();
  const formats = capabilities && labelsFor(capabilities.upload.allowedMimeTypes);
  // Silence would read as "no restrictions"; the prose that used to list them is gone.
  const formatsNote = formats
    ? t('help.supportedFormats', { types: formats })
    : (status === 'error' ? t('help.supportedFormatsUnavailable') : '');
  const designVerificationEnabled = useAppSelector(selectDesignVerificationEnabled);

  // A disabled tenant must not read a feature card for 設計検証, and above all
  // must not be told by the FAQ to "access it from the sidebar 設計検証項目" —
  // an item this build removes for them (#1214).
  const showVerification = designVerificationEnabled !== false;
  const features = HELP_FEATURES.filter(({ id }) => id !== 'verification' || showVerification);
  const faqIds = HELP_FAQ_IDS.filter((id) => id !== 'verification' || showVerification);

  return (
    <div className="max-w-4xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-balance sm:text-3xl">
          {t('help.title')}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground text-pretty">{t('help.description')}</p>
      </header>

      <Card>
        <CardHeader className="flex-row items-center gap-2.5 space-y-0 pb-4">
          <BookOpen className="size-[18px] text-primary" strokeWidth={1.9} />
          <CardTitle className="text-base">{t('help.featuresTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="grid gap-3 sm:grid-cols-2">
            {features.map(({ id, icon: Icon, titleKey, descriptionKey }) => (
              <li key={id} className="flex gap-3 rounded-lg border bg-muted/20 p-3">
                <span className="grid size-8 shrink-0 place-items-center rounded-md bg-primary/10 text-primary">
                  <Icon className="size-4" strokeWidth={1.9} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium leading-snug">{t(titleKey)}</p>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground text-pretty">
                    {t(descriptionKey)}
                    {id === 'upload' && formatsNote && ` ${formatsNote}`}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center gap-2.5 space-y-0 pb-2">
          <MessageCircleQuestion className="size-[18px] text-primary" strokeWidth={1.9} />
          <CardTitle className="text-base">{t('help.faqTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            {faqIds.map((id) => (
              <AccordionItem key={id} value={id}>
                <AccordionTrigger className="gap-3 text-left hover:no-underline">
                  <span className="flex min-w-0 flex-1 items-center gap-2.5">
                    <Badge variant="secondary" className="shrink-0 font-normal">
                      {t(`help.categories.${id}`, id)}
                    </Badge>
                    <span className="min-w-0 text-sm font-medium text-pretty">
                      {t(`help.faq.${id}Question`)}
                    </span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pl-1 text-sm leading-relaxed text-muted-foreground text-pretty">
                  {t(`help.faq.${id}Answer`)}
                  {id === 'upload' && formatsNote && <p className="mt-2">{formatsNote}</p>}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center gap-2.5 space-y-0 pb-4">
          <LifeBuoy className="size-[18px] text-primary" strokeWidth={1.9} />
          <CardTitle className="text-base">{t('help.tipsTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2.5">
            {HELP_TIP_KEYS.map((key) => (
              <li key={key} className="flex gap-2.5 text-sm leading-relaxed text-pretty">
                <Check className="mt-0.5 size-4 shrink-0 text-success" strokeWidth={2.2} />
                {t(key)}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('help.contactTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm leading-relaxed text-muted-foreground text-pretty">
            {t('help.contactDescription')}
          </p>
          <Button variant="outline" asChild>
            <a href={`mailto:${HELP_CONTACT_EMAIL}`}>
              <Mail strokeWidth={1.9} />
              {HELP_CONTACT_EMAIL}
            </a>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default HelpPage;
